import { getPgaCanonicalIdentity } from "./pgaCanonicalIdentity";
import { assignCs2MatchKeys } from "./cs2MatchIdentity";
import { underlyingEventKey } from "./underlyingEventIdentity";

const MLB_HITTER_UNDER_STATS = [
  "hits",
  "singles",
  "doubles",
  "home_runs",
  "total_bases",
  "runs_batted_in",
  "runs",
  "hits+runs+rbis",
];

const BASKETBALL_OFFENSE_STATS = [
  "points",
  "assists",
  "rebounds",
  "pra",
  "pts+rebs",
  "pts+asts",
  "rebs+asts",
  "3ptm",
  "field_goals_made",
];

const BASKETBALL_PACE_STATS = [
  "points",
  "assists",
  "rebounds",
  "pra",
  "pts+rebs",
  "pts+asts",
  "rebs+asts",
];

const NFL_QB_PASSING_STATS = [
  "passing_yards",
  "passing_touchdowns",
  "passing_completions",
];

const NFL_RECEIVER_STATS = [
  "receiving_yards",
  "receptions",
  "rec_targets",
];

const SOCCER_ATTACKING_STATS = [
  "sot",
  "shots",
  "goals",
  "goals+assists",
  "assists",
  "shots_assisted",
];

const BASKETBALL_LEAGUES = ["nba", "wnba", "wnba1h", "cbb", "ncaamb", "ncaawb"];
const FOOTBALL_LEAGUES = ["nfl", "cfb"];

export const SAME_GAME_CORRELATION_RULES = {
  mlb: [
    {
      id: "mlb_pitcher_outs_overs",
      haircutPct: 0.15,
      maxEntry: 10,
      reason: "Same-game pitcher outs overs are correlated with low-scoring/efficient game script.",
    },
    {
      id: "mlb_pitcher_outs_unders",
      haircutPct: 0.10,
      maxEntry: 15,
      reason: "Same-game pitcher outs unders can correlate with high-offense/short-start game script.",
    },
    {
      id: "mlb_pitcher_outs_over_opposing_hitter_under",
      haircutPct: 0.10,
      maxEntry: 15,
      reason: "Pitcher depth over is correlated with opposing hitter unders.",
    },
  ],
  basketball: [
    {
      id: "basketball_same_team_offensive_overs",
      haircutPct: 0.10,
      maxEntry: 20,
      reason: "Same-team offensive overs can share pace/usage correlation.",
    },
    {
      id: "basketball_same_game_pace_overs",
      haircutPct: 0.15,
      maxEntry: 15,
      reason: "Multiple same-game overs correlate with pace/game environment.",
    },
    {
      id: "basketball_same_game_three_plus",
      haircutPct: 0.15,
      maxEntry: 15,
      reason: "Three or more same-game basketball selections share game-environment correlation.",
    },
  ],
  football: [
    {
      id: "football_qb_receiver_overs",
      haircutPct: 0.20,
      maxEntry: 15,
      reason: "QB passing overs correlate with receiver overs.",
    },
    {
      id: "football_rb_rushing_opponent_rushing_under_placeholder",
      enforce: false,
    },
  ],
  soccer: [
    {
      id: "soccer_attacking_stack",
      haircutPct: 0.20,
      maxEntry: 10,
      reason: "Soccer attacking props are correlated and low-liquidity.",
    },
    {
      id: "soccer_goalie_saves_opposing_shots",
      haircutPct: 0.25,
      maxEntry: 10,
      reason: "Goalie saves over directly correlates with opposing shot volume.",
    },
  ],
};

const STAT_ALIASES = {
  po: "total_outs_pitched",
  pitching_outs: "total_outs_pitched",
  pitch_outs: "total_outs_pitched",
  pitcher_outs: "total_outs_pitched",
  outs_pitched: "total_outs_pitched",
  tb: "total_bases",
  hr: "home_runs",
  homeruns: "home_runs",
  rbi: "runs_batted_in",
  hrr: "hits+runs+rbis",
  hits_runs_rbis: "hits+runs+rbis",
  hits_runs_rbi: "hits+runs+rbis",
  points_rebounds_assists: "pra",
  pts_rebs_asts: "pra",
  points_rebounds: "pts+rebs",
  points_assists: "pts+asts",
  rebounds_assists: "rebs+asts",
  threes_made: "3ptm",
  three_pointers_made: "3ptm",
  made_threes: "3ptm",
  fgm: "field_goals_made",
  pass_yards: "passing_yards",
  passing_yds: "passing_yards",
  pass_tds: "passing_touchdowns",
  passing_tds: "passing_touchdowns",
  completions: "passing_completions",
  rec_yards: "receiving_yards",
  receiving_yds: "receiving_yards",
  receptions_made: "receptions",
  targets: "rec_targets",
  shots_on_goal: "sot",
  shots_on_target: "sot",
  sog: "sot",
  goalie_saves: "goalie_saves",
  saves: "goalie_saves",
};

const clampScore = (score) => Math.min(1, Number(score.toFixed(2)));
const capHaircut = (haircutPct) => Math.min(0.35, Number(haircutPct.toFixed(2)));

const normalizeText = (value) => String(value || "").trim().toLowerCase();

const minEntry = (currentMaxEntry, cap) => {
  if (!Number.isFinite(Number(cap))) {
    return currentMaxEntry;
  }

  if (currentMaxEntry === null || currentMaxEntry === undefined || currentMaxEntry === "") {
    return cap;
  }

  const parsedMax = Number(currentMaxEntry);
  return Number.isFinite(parsedMax) ? Math.min(parsedMax, cap) : cap;
};

const addReason = (result, reason) => {
  if (reason && !result.reasons.includes(reason)) {
    result.reasons.push(reason);
  }
};

const getAthleteIdentity = (selection) => {
  const athleteId = selection?.athlete_id ?? selection?.athleteId ?? selection?.id;
  if (athleteId !== undefined && athleteId !== null && String(athleteId).trim() !== "") {
    return `id:${athleteId}`;
  }

  return `name:${normalizeText(selection?.name || selection?.athlete_name)}`;
};

const hasDifferentAthletes = (selectionGroup) =>
  new Set(selectionGroup.map(getAthleteIdentity).filter(Boolean)).size > 1;

const getLeague = (selection) => {
  const league = normalizeText(selection?.league || selection?.sport);
  if (["mls", "epl", "premier_league", "laliga", "seriea", "ligue1", "worldcup", "worldcup1h", "worldcup2h"].includes(league)) {
    return "soccer";
  }

  return league;
};

export const normalizeStatKey = (statKey) => {
  const raw = normalizeText(statKey)
    .replace(/^projected_/, "")
    .replace(/^promo_/, "")
    .replace(/^goblin_/, "")
    .replace(/^demon_/, "")
    .replace(/\s+/g, "_")
    .replace(/\//g, "+")
    .replace(/-/g, "_");

  return STAT_ALIASES[raw] || raw;
};

export const normalizeDirection = (direction) => {
  const normalized = normalizeText(direction);

  if (["more", "over", "o", "higher", "yes"].includes(normalized)) {
    return "more";
  }

  if (["less", "under", "u", "lower", "no"].includes(normalized)) {
    return "less";
  }

  return normalized;
};

export const getGameKey = (selection) => {
  const league = getLeague(selection);
  if (league === "pga") {
    const pgaIdentity = getPgaCanonicalIdentity(selection);
    if (pgaIdentity) {
      return pgaIdentity.eventKey;
    }
  }
  const gameId = selection?.game_id ?? selection?.gameId ?? selection?.event_id ?? selection?.eventId;
  if (gameId !== undefined && gameId !== null && String(gameId).trim() !== "" && String(gameId) !== "unknown") {
    return underlyingEventKey(`${league}|game:${String(gameId).trim()}`);
  }

  let matchup = normalizeText(selection?.matchup || selection?.game);
  if (!matchup) {
    const teams = [getTeam(selection), getOpponent(selection)].filter(Boolean).sort();
    matchup = teams.length === 2 ? teams.join("-") : normalizeText(selection?.opponent || selection?.opponent_short);
  }
  const startTime = normalizeText(selection?.start_time_iso || selection?.start_time_obj || selection?.start_time);
  if (league && (matchup || startTime)) {
    return `${league}|matchup:${matchup}|start:${startTime}`;
  }

  return "";
};

export const getGameKeys = (selected = []) => {
  const selections = Array.isArray(selected) ? selected : [];
  const keys = selections.map(getGameKey);
  const cs2Indexes = selections.reduce((indexes, selection, index) => {
    if (getLeague(selection) === "cs2") indexes.push(index);
    return indexes;
  }, []);
  if (cs2Indexes.length > 0) {
    const cs2Keys = assignCs2MatchKeys(cs2Indexes.map((index) => selections[index]));
    cs2Indexes.forEach((selectionIndex, index) => {
      keys[selectionIndex] = `cs2|${cs2Keys[index]}`;
    });
  }
  return keys;
};

export const getSameGameEntryLimitMessage = (selected = []) => {
  const scoringMessage = getBasketballScoringFamilyLimitMessage(selected);
  if (scoringMessage) return scoringMessage;
  const counts = getGameKeys(selected).filter(Boolean).reduce((result, key) => {
    result[key] = (result[key] || 0) + 1;
    return result;
  }, {});
  if (selected.length >= 4 && Object.values(counts).some((count) => count > 3)) {
    return "Too many picks from the same game. Entries with 4 or more picks can include only 3 picks from one matchup.";
  }
  if (selected.length >= 6 && Object.keys(counts).length < 3) {
    return "Six-pick entries require picks from at least 3 different games. Replace a pick with one from another game.";
  }
  return "";
};

export const isPromoSelection = (selection) =>
  Boolean(selection?.promo || selection?.free_square || selection?.freeSquare || selection?.boosted || selection?.boost);

const getTeam = (selection) => normalizeText(selection?.team_short || selection?.team || selection?.team_abbr);
const getOpponent = (selection) => normalizeText(selection?.opponent_short || selection?.opponent || selection?.opponent_abbr);

export const areSameTeam = (a, b) => {
  const teamA = getTeam(a);
  const teamB = getTeam(b);
  return Boolean(teamA && teamB && teamA === teamB);
};

export const areOpposingTeams = (a, b) => {
  const teamA = getTeam(a);
  const teamB = getTeam(b);
  const opponentA = getOpponent(a);
  const opponentB = getOpponent(b);

  if (!teamA || !teamB || teamA === teamB) {
    return false;
  }

  return opponentA === teamB || opponentB === teamA || getGameKey(a) === getGameKey(b);
};

export const hasAnyStat = (selectionGroup, statList) => {
  const statSet = new Set(statList.map(normalizeStatKey));
  return selectionGroup.some((selection) => statSet.has(normalizeStatKey(selection?.stat_key || selection?.stat)));
};

export const allStatsAre = (selectionGroup, statList) => {
  const statSet = new Set(statList.map(normalizeStatKey));
  return selectionGroup.length > 0 &&
    selectionGroup.every((selection) => statSet.has(normalizeStatKey(selection?.stat_key || selection?.stat)));
};

export const allDirectionsAre = (selectionGroup, direction) =>
  selectionGroup.length > 0 &&
  selectionGroup.every((selection) => normalizeDirection(selection?.selected || selection?.direction || selection?.pick_type) === direction);

const groupSelectionsByGame = (selected) => {
  const gameKeys = getGameKeys(selected);
  return selected.reduce((groups, selection, index) => {
    const gameKey = gameKeys[index];
    if (!gameKey) {
      return groups;
    }

    groups[gameKey] = groups[gameKey] || [];
    groups[gameKey].push(selection);
    return groups;
  }, {});
};

// All sides/variants count; this limits concentration, not inferred profitability.
export const getBasketballScoringFamilyLimitMessage = (selected = []) => {
  const counts = new Map();
  for (const selection of selected) {
    if (!BASKETBALL_LEAGUES.includes(getLeague(selection))) continue;
    if (!["points", "pra", "pts+rebs", "pts+asts"].includes(normalizeStatKey(selection?.stat_key || selection?.stat))) continue;
    const key = getGameKey(selection);
    if (!key) continue;
    const count = (counts.get(key) || 0) + 1;
    counts.set(key, count);
    if (count > 2) {
      return "Choose up to 2 Points, PRA, Points + Rebounds, or Points + Assists picks from one basketball game. Replace a pick with a different stat or a pick from another game.";
    }
  }
  return "";
};

const applyRule = (result, rule, scoreParts = {}) => {
  if (!rule || rule.enforce === false) {
    return;
  }

  result.haircutPct += Number(rule.haircutPct || 0);
  result.maxEntry = minEntry(result.maxEntry, rule.maxEntry);
  addReason(result, rule.reason);

  result._scoreParts.sameGame = true;
  Object.entries(scoreParts).forEach(([key, value]) => {
    if (value) {
      result._scoreParts[key] = true;
    }
  });
};

const hasPair = (group, predicate) => {
  for (let i = 0; i < group.length; i += 1) {
    for (let j = i + 1; j < group.length; j += 1) {
      if (getAthleteIdentity(group[i]) !== getAthleteIdentity(group[j]) && predicate(group[i], group[j])) {
        return true;
      }
    }
  }

  return false;
};

const evaluateMlbGroup = (group, result) => {
  if (!hasDifferentAthletes(group)) {
    return;
  }

  if (allStatsAre(group, ["total_outs_pitched"]) && allDirectionsAre(group, "more")) {
    applyRule(result, SAME_GAME_CORRELATION_RULES.mlb[0], {
      sameStatGroup: true,
      sameDirection: true,
    });
  }

  if (allStatsAre(group, ["total_outs_pitched"]) && allDirectionsAre(group, "less")) {
    applyRule(result, SAME_GAME_CORRELATION_RULES.mlb[1], {
      sameStatGroup: true,
      sameDirection: true,
    });
  }

  const hasPitcherHitterPair = hasPair(group, (a, b) => {
    const checks = [
      [a, b],
      [b, a],
    ];

    return checks.some(([pitcher, hitter]) =>
      normalizeStatKey(pitcher?.stat_key || pitcher?.stat) === "total_outs_pitched" &&
      normalizeDirection(pitcher?.selected || pitcher?.direction || pitcher?.pick_type) === "more" &&
      MLB_HITTER_UNDER_STATS.includes(normalizeStatKey(hitter?.stat_key || hitter?.stat)) &&
      normalizeDirection(hitter?.selected || hitter?.direction || hitter?.pick_type) === "less" &&
      areOpposingTeams(pitcher, hitter)
    );
  });

  if (hasPitcherHitterPair) {
    applyRule(result, SAME_GAME_CORRELATION_RULES.mlb[2], {
      sameDirection: false,
      opposingDirectStatRelationship: true,
    });
  }
};

const evaluateBasketballGroup = (group, result) => {
  if (group.length >= 3) result.maxWagerOverride = 15;
  if (!hasDifferentAthletes(group)) {
    return;
  }

  const sameTeamOffense = hasPair(group, (a, b) =>
    areSameTeam(a, b) &&
    normalizeDirection(a?.selected || a?.direction || a?.pick_type) === "more" &&
    normalizeDirection(b?.selected || b?.direction || b?.pick_type) === "more" &&
    BASKETBALL_OFFENSE_STATS.includes(normalizeStatKey(a?.stat_key || a?.stat)) &&
    BASKETBALL_OFFENSE_STATS.includes(normalizeStatKey(b?.stat_key || b?.stat))
  );

  if (sameTeamOffense) {
    applyRule(result, SAME_GAME_CORRELATION_RULES.basketball[0], {
      sameStatGroup: true,
      sameDirection: true,
      sameTeam: true,
    });
  }

  const paceOvers = group.filter((selection) =>
    normalizeDirection(selection?.selected || selection?.direction || selection?.pick_type) === "more" &&
    BASKETBALL_PACE_STATS.includes(normalizeStatKey(selection?.stat_key || selection?.stat))
  );
  const paceTeams = new Set(paceOvers.map(getTeam).filter(Boolean));
  const paceAthletes = new Set(paceOvers.map(getAthleteIdentity).filter(Boolean));

  if (paceOvers.length >= 3 && paceTeams.size >= 2 && paceAthletes.size >= 2) {
    applyRule(result, SAME_GAME_CORRELATION_RULES.basketball[1], {
      sameStatGroup: true,
      sameDirection: true,
    });
  }

  if (group.length >= 3) {
    applyRule(result, SAME_GAME_CORRELATION_RULES.basketball[2]);
  }
};

const evaluateFootballGroup = (group, result) => {
  if (!hasDifferentAthletes(group)) {
    return;
  }

  const qbReceiverPair = hasPair(group, (a, b) => {
    const checks = [
      [a, b],
      [b, a],
    ];

    return checks.some(([qb, receiver]) =>
      areSameTeam(qb, receiver) &&
      normalizeDirection(qb?.selected || qb?.direction || qb?.pick_type) === "more" &&
      normalizeDirection(receiver?.selected || receiver?.direction || receiver?.pick_type) === "more" &&
      NFL_QB_PASSING_STATS.includes(normalizeStatKey(qb?.stat_key || qb?.stat)) &&
      NFL_RECEIVER_STATS.includes(normalizeStatKey(receiver?.stat_key || receiver?.stat))
    );
  });

  if (qbReceiverPair) {
    applyRule(result, SAME_GAME_CORRELATION_RULES.football[0], {
      sameStatGroup: true,
      sameDirection: true,
      sameTeam: true,
    });
  }
};

const evaluateSoccerGroup = (group, result) => {
  if (!hasDifferentAthletes(group)) {
    return;
  }

  const attackingOversByTeam = group.reduce((teams, selection) => {
    if (
      normalizeDirection(selection?.selected || selection?.direction || selection?.pick_type) !== "more" ||
      !SOCCER_ATTACKING_STATS.includes(normalizeStatKey(selection?.stat_key || selection?.stat))
    ) {
      return teams;
    }

    const team = getTeam(selection);
    if (!team) {
      return teams;
    }

    teams[team] = teams[team] || [];
    teams[team].push(selection);
    return teams;
  }, {});

  if (Object.values(attackingOversByTeam).some((entries) => hasDifferentAthletes(entries) && entries.length >= 2)) {
    applyRule(result, SAME_GAME_CORRELATION_RULES.soccer[0], {
      sameStatGroup: true,
      sameDirection: true,
      sameTeam: true,
      lowLiquiditySport: true,
    });
  }

  const goalieShotPair = hasPair(group, (a, b) => {
    const checks = [
      [a, b],
      [b, a],
    ];

    return checks.some(([goalie, shooter]) =>
      normalizeStatKey(goalie?.stat_key || goalie?.stat) === "goalie_saves" &&
      normalizeDirection(goalie?.selected || goalie?.direction || goalie?.pick_type) === "more" &&
      ["shots", "sot"].includes(normalizeStatKey(shooter?.stat_key || shooter?.stat)) &&
      normalizeDirection(shooter?.selected || shooter?.direction || shooter?.pick_type) === "more" &&
      areOpposingTeams(goalie, shooter)
    );
  });

  if (goalieShotPair) {
    applyRule(result, SAME_GAME_CORRELATION_RULES.soccer[1], {
      sameStatGroup: true,
      sameDirection: true,
      opposingDirectStatRelationship: true,
      lowLiquiditySport: true,
    });
  }
};

const applyScoreAndThresholds = (result, selected, userTier) => {
  const hasPromoPick = selected.some(isPromoSelection);
  const hasCorrelation = result.reasons.length > 0;

  if (hasCorrelation && hasPromoPick) {
    result.haircutPct += 0.10;
    result.maxEntry = minEntry(result.maxEntry, 10);
    result._scoreParts.promo = true;
  }

  if (hasCorrelation && userTier >= 2) {
    result.haircutPct += 0.10;
    result.maxEntry = minEntry(result.maxEntry, 10);
    result._scoreParts.userTier = true;
  }

  if (hasCorrelation && userTier >= 3) {
    result.maxEntry = minEntry(result.maxEntry, 5);
  }

  let score = 0;
  if (result._scoreParts.sameGame) score += 0.15;
  if (result._scoreParts.sameStatGroup) score += 0.20;
  if (result._scoreParts.sameDirection) score += 0.15;
  if (result._scoreParts.sameTeam) score += 0.10;
  if (result._scoreParts.opposingDirectStatRelationship) score += 0.20;
  if (result._scoreParts.promo) score += 0.15;
  if (result._scoreParts.userTier) score += 0.15;
  if (result._scoreParts.lowLiquiditySport) score += 0.15;

  result.correlationScore = clampScore(score);

  if (result.correlationScore >= 0.65) {
    result.haircutPct = Math.max(result.haircutPct, 0.25);
    result.maxEntry = minEntry(result.maxEntry, 10);
  } else if (result.correlationScore >= 0.45) {
    result.haircutPct = Math.max(result.haircutPct, 0.15);
    result.maxEntry = minEntry(result.maxEntry, 15);
  } else if (result.correlationScore >= 0.25) {
    result.haircutPct = Math.max(result.haircutPct, 0.10);
  }

  result.haircutPct = capHaircut(result.haircutPct);
  result.shouldBlock = result.correlationScore >= 0.75 && hasPromoPick && userTier >= 2;
};

export function evaluateSameGameCorrelation(selected, userTier = 0, currentMaxEntry = null) {
  const result = {
    correlationScore: 0,
    haircutPct: 0,
    maxEntry: currentMaxEntry,
    shouldBlock: false,
    reasons: [],
    _scoreParts: {},
  };

  if (!Array.isArray(selected) || selected.length < 2) {
    delete result._scoreParts;
    return result;
  }

  const groups = Object.values(groupSelectionsByGame(selected)).filter((group) => group.length >= 2);

  groups.forEach((group) => {
    const league = getLeague(group[0]);
    if (league === "mlb") {
      evaluateMlbGroup(group, result);
    } else if (BASKETBALL_LEAGUES.includes(league)) {
      evaluateBasketballGroup(group, result);
    } else if (FOOTBALL_LEAGUES.includes(league)) {
      evaluateFootballGroup(group, result);
    } else if (league === "soccer") {
      evaluateSoccerGroup(group, result);
    }
  });

  applyScoreAndThresholds(result, selected, Number(userTier) || 0);
  const scoringMessage = getBasketballScoringFamilyLimitMessage(selected);
  if (scoringMessage) {
    result.shouldBlock = true;
    result.blockMessage = scoringMessage;
    addReason(result, scoringMessage);
  }
  delete result._scoreParts;
  return result;
}
